"use client"

import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react"
import { getSupabaseBrowserClient } from "@/utils/supabase/client"
import {
  toFiniteNumber,
  sortBids,
  normalizeBidRow,
  normalizeChatItem,
} from "@/lib/auction-utils"
import { syncClock } from "@/lib/clock-sync"
import type { AuctionSlot, AuctionChatItem, BidRow } from "@/lib/auction-types"

// ---------------------------------------------------------------------------
// Context value interface
// ---------------------------------------------------------------------------

export interface AuctionRealtimeContextValue {
  subscribe(id: string, initial?: Partial<AuctionSlot>): void
  unsubscribe(id: string): void
  subscribeBroadcast(id: string): void
  unsubscribeBroadcast(id: string): void
  patchAuction(id: string, partial: Partial<AuctionSlot>): void
  getSnapshot(id: string): AuctionSlot | null
  addListener(id: string, fn: () => void): () => void
  refetchBids(id: string): void
  refetchChat(id: string): void
  refetchSnapshot(id: string): void
  sendChatMessage(id: string, item: AuctionChatItem): void
}

export const AuctionRealtimeContext = createContext<AuctionRealtimeContextValue | null>(null)

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface BroadcastEntry {
  channel: ReturnType<ReturnType<typeof getSupabaseBrowserClient>["channel"]>
  refCount: number
  timer?: ReturnType<typeof setTimeout>
}

// ---------------------------------------------------------------------------
// Default slot factory
// ---------------------------------------------------------------------------

function defaultSlot(initial?: Partial<AuctionSlot>): AuctionSlot {
  const base: AuctionSlot = {
    highestBid: null,
    highestBidderId: null,
    bidCount: 0,
    endAt: null,
    auctionStatus: null,
    eventVersion: 0,
    paymentDeadline: null,
    bids: [],
    chatItems: [],
    isConnected: false,
  }
  if (!initial) return base
  return { ...base, ...initial, paymentDeadline: initial.paymentDeadline ?? null }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuctionRealtimeProvider({ children }: { children: React.ReactNode }) {
  // State store — useRef, not useState, to avoid cascading re-renders
  const auctionsRef = useRef(new Map<string, AuctionSlot>())
  // Per-id subscriber sets for useSyncExternalStore
  const listenersRef = useRef(new Map<string, Set<() => void>>())
  // eventVersion dedup: tracks the highest eventVersion seen per listing (from any source)
  const evRef = useRef(new Map<string, number>())
  // On-demand broadcast channels with refcounting
  const broadcastRefs = useRef(new Map<string, BroadcastEntry>())
  // Per-listing pending fetch promises for dedup
  const chatFetchRef = useRef(new Map<string, Promise<AuctionChatItem[]>>())
  const bidsFetchRef = useRef(new Map<string, Promise<BidRow[]>>())
  // Per-listing known bid ids to prevent duplicates
  const knownBidIdsRef = useRef(new Map<string, Set<string>>())

  // -------------------------------------------------------------------------
  // Core: notify listeners for a given listing id
  // -------------------------------------------------------------------------

  const notify = useCallback((id: string) => {
    listenersRef.current.get(id)?.forEach((fn) => fn())
  }, [])

  // -------------------------------------------------------------------------
  // patchAuction: merge partial into slot, respect eventVersion monotonicity
  // -------------------------------------------------------------------------

  const patchAuction = useCallback((id: string, partial: Partial<AuctionSlot>) => {
    const current = auctionsRef.current.get(id)
    if (!current) return

    let next = { ...current, ...partial }

    // eventVersion must be monotonically increasing
    if (partial.eventVersion != null) {
      next.eventVersion = Math.max(current.eventVersion, partial.eventVersion)
    }

    auctionsRef.current.set(id, next)
    notify(id)
  }, [notify])

  // -------------------------------------------------------------------------
  // subscribe / unsubscribe (refcount for Map membership)
  // -------------------------------------------------------------------------

  const subscribeCountsRef = useRef(new Map<string, number>())

  const subscribe = useCallback((id: string, initial?: Partial<AuctionSlot>) => {
    const count = (subscribeCountsRef.current.get(id) ?? 0) + 1
    subscribeCountsRef.current.set(id, count)

    if (!auctionsRef.current.has(id)) {
      auctionsRef.current.set(id, defaultSlot(initial))
    } else if (initial) {
      // Merge initial only for fields that improve our knowledge (higher eventVersion)
      const current = auctionsRef.current.get(id)!
      if (
        initial.eventVersion != null &&
        initial.eventVersion > current.eventVersion
      ) {
        auctionsRef.current.set(id, { ...current, ...initial, eventVersion: initial.eventVersion })
        notify(id)
      }
    }

    if (!listenersRef.current.has(id)) {
      listenersRef.current.set(id, new Set())
    }
  }, [notify])

  const unsubscribe = useCallback((id: string) => {
    const count = (subscribeCountsRef.current.get(id) ?? 1) - 1
    if (count <= 0) {
      subscribeCountsRef.current.delete(id)
      listenersRef.current.delete(id)
      knownBidIdsRef.current.delete(id)
      auctionsRef.current.delete(id)
      evRef.current.delete(id)
    } else {
      subscribeCountsRef.current.set(id, count)
    }
  }, [])

  // -------------------------------------------------------------------------
  // addListener (returns cleanup)
  // -------------------------------------------------------------------------

  const addListener = useCallback((id: string, fn: () => void): (() => void) => {
    if (!listenersRef.current.has(id)) {
      listenersRef.current.set(id, new Set())
    }
    listenersRef.current.get(id)!.add(fn)
    return () => {
      listenersRef.current.get(id)?.delete(fn)
    }
  }, [])

  // -------------------------------------------------------------------------
  // getSnapshot
  // -------------------------------------------------------------------------

  const getSnapshot = useCallback((id: string): AuctionSlot | null => {
    return auctionsRef.current.get(id) ?? null
  }, [])

  // -------------------------------------------------------------------------
  // Chat item append (dedup by id)
  // -------------------------------------------------------------------------

  const appendChatItem = useCallback((id: string, item: AuctionChatItem) => {
    const slot = auctionsRef.current.get(id)
    if (!slot) return
    const existing = slot.chatItems ?? []
    if (existing.some((c) => c.id === item.id)) return
    auctionsRef.current.set(id, { ...slot, chatItems: [...existing, item] })
    notify(id)
  }, [notify])

  // -------------------------------------------------------------------------
  // sendChatMessage (optimistic local append)
  // -------------------------------------------------------------------------

  const sendChatMessage = useCallback((id: string, item: AuctionChatItem) => {
    appendChatItem(id, item)
  }, [appendChatItem])

  // -------------------------------------------------------------------------
  // Bid row append (dedup by id using per-listing Set)
  // -------------------------------------------------------------------------

  const appendBidRow = useCallback((id: string, bidRow: BidRow): boolean => {
    if (!bidRow?.id) return false
    let knownIds = knownBidIdsRef.current.get(id)
    if (!knownIds) {
      knownIds = new Set()
      knownBidIdsRef.current.set(id, knownIds)
    }
    if (knownIds.has(bidRow.id)) return false
    knownIds.add(bidRow.id)

    const slot = auctionsRef.current.get(id)
    if (!slot) return false
    const existing = slot.bids ?? []
    if (existing.some((b) => b.id === bidRow.id)) return false
    auctionsRef.current.set(id, { ...slot, bids: sortBids([...existing, bidRow]) })
    notify(id)
    return true
  }, [notify])

  // -------------------------------------------------------------------------
  // Fetchers with dedup and 10s AbortController timeout
  // -------------------------------------------------------------------------

  const refetchChat = useCallback((id: string) => {
    if (chatFetchRef.current.has(id)) return
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 10_000)
    const p: Promise<AuctionChatItem[]> = fetch(`/api/auctions/${id}/chat`, { signal: controller.signal })
      .then((r) => r.json())
      .then((d) => (Array.isArray(d?.items) ? (d.items as unknown[]) : []))
      .then((rows) =>
        rows.map(normalizeChatItem).filter((item): item is AuctionChatItem => item != null)
      )
      .catch(() => [])
      .finally(() => {
        clearTimeout(timer)
        chatFetchRef.current.delete(id)
      })
    chatFetchRef.current.set(id, p)
    p.then((items) => {
      if (!auctionsRef.current.has(id)) return
      const slot = auctionsRef.current.get(id)!
      const existingIds = new Set((slot.chatItems ?? []).map((c) => c.id))
      const merged = [...(slot.chatItems ?? [])]
      for (const item of items) {
        if (!existingIds.has(item.id)) merged.push(item)
      }
      auctionsRef.current.set(id, { ...slot, chatItems: merged })
      notify(id)
    })
  }, [notify])

  const refetchBids = useCallback((id: string) => {
    if (bidsFetchRef.current.has(id)) return
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 10_000)
    const p: Promise<BidRow[]> = fetch(`/api/auctions/${id}/bids`, { signal: controller.signal })
      .then((r) => r.json())
      .then((d) => (Array.isArray(d?.bids) ? (d.bids as unknown[]) : []))
      .then((rows) =>
        rows.map(normalizeBidRow).filter((row): row is BidRow => row != null)
      )
      .then((rows) => sortBids(rows))
      .catch(() => [])
      .finally(() => {
        clearTimeout(timer)
        bidsFetchRef.current.delete(id)
      })
    bidsFetchRef.current.set(id, p)
    p.then((rows) => {
      if (!auctionsRef.current.has(id)) return
      // Rebuild known ids from fresh fetch
      const knownIds = new Set(rows.map((r) => r.id))
      knownBidIdsRef.current.set(id, knownIds)
      const slot = auctionsRef.current.get(id)!
      auctionsRef.current.set(id, { ...slot, bids: rows })
      notify(id)
    })
  }, [notify])

  const refetchSnapshot = useCallback((id: string) => {
    const supabase = getSupabaseBrowserClient()
    ;(async () => {
      try {
        const { data } = await supabase
          .from("listing_auctions")
          .select(
            "end_at, highest_bid, highest_bidder_id, bid_count, event_version, status, payment_deadline"
          )
          .eq("listing_id", id)
          .maybeSingle()
        if (!data || !auctionsRef.current.has(id)) return
        const slot = auctionsRef.current.get(id)!
        const nextEndAt = data.end_at == null ? null : String(data.end_at)
        const nextHighestBid = toFiniteNumber(data.highest_bid)
        const nextBidCount = toFiniteNumber(data.bid_count)
        const nextEventVersion = toFiniteNumber(data.event_version)
        auctionsRef.current.set(id, {
          ...slot,
          endAt: nextEndAt,
          highestBid: data.highest_bid == null ? null : (nextHighestBid ?? slot.highestBid),
          highestBidderId:
            typeof data.highest_bidder_id === "string"
              ? data.highest_bidder_id
              : null,
          auctionStatus:
            typeof data.status === "string" ? data.status : slot.auctionStatus,
          bidCount: nextBidCount ?? slot.bidCount,
          eventVersion:
            nextEventVersion != null
              ? Math.max(slot.eventVersion, nextEventVersion)
              : slot.eventVersion,
          paymentDeadline:
            typeof (data as any).payment_deadline === "string"
              ? (data as any).payment_deadline
              : slot.paymentDeadline,
        })
        if (nextEventVersion != null) {
          evRef.current.set(id, Math.max(evRef.current.get(id) ?? 0, nextEventVersion))
        }
        notify(id)
      } catch {
        // ignore
      }
    })()
  }, [notify])

  // -------------------------------------------------------------------------
  // applyBidRealtimePayload — ported from use-auction-realtime.ts lines 222-265
  // -------------------------------------------------------------------------

  const applyBidRealtimePayload = useCallback(
    (id: string, payload: Record<string, unknown>) => {
      const slot = auctionsRef.current.get(id)
      if (!slot) return

      let next = { ...slot }

      if (Object.prototype.hasOwnProperty.call(payload, "new_end_at")) {
        const payloadEndAt = payload.new_end_at
        if (payloadEndAt == null) {
          next.endAt = null
        } else if (typeof payloadEndAt === "string") {
          next.endAt = payloadEndAt
        }
      }

      if (Object.prototype.hasOwnProperty.call(payload, "new_highest_bid")) {
        const nextHighestBid = toFiniteNumber(payload.new_highest_bid)
        if (nextHighestBid != null) next.highestBid = nextHighestBid
        if (payload.new_highest_bid == null) next.highestBid = null
      }

      if (Object.prototype.hasOwnProperty.call(payload, "event_version")) {
        const nextEventVersion = toFiniteNumber(payload.event_version)
        if (nextEventVersion != null) {
          next.eventVersion = Math.max(slot.eventVersion, nextEventVersion)
          evRef.current.set(id, Math.max(evRef.current.get(id) ?? 0, nextEventVersion))
        }
      }

      const exactBidCount = toFiniteNumber(payload.new_bid_count)
      const bidRow = normalizeBidRow(payload.bid_row)

      // FIX-8: update highestBidderId from bid_row when new highest bid arrives
      if (bidRow && typeof bidRow.bidder_id === "string" && bidRow.bidder_id.length > 0) {
        next.highestBidderId = bidRow.bidder_id
      }

      auctionsRef.current.set(id, next)

      const added = bidRow ? appendBidRow(id, bidRow) : false

      // Also add bid to chatItems so it appears in the merged timeline instantly
      // Always try — appendChatItem has its own dedup by item.id
      if (bidRow) {
        appendChatItem(id, {
          type: "bid",
          id: bidRow.id,
          created_at: bidRow.created_at,
          user_id: bidRow.bidder_id ?? "",
          username: bidRow.bidder_display,
          avatar_url: bidRow.avatar_url ?? null,
          amount: bidRow.amount,
        })
      }

      if (!bidRow) {
        // Safety net: no bid_row in payload — refetch
        refetchBids(id)
        refetchSnapshot(id)
      }

      if (exactBidCount != null) {
        const s = auctionsRef.current.get(id)
        if (s) {
          auctionsRef.current.set(id, { ...s, bidCount: exactBidCount })
        }
      } else if (added) {
        const s = auctionsRef.current.get(id)
        if (s) {
          auctionsRef.current.set(id, { ...s, bidCount: s.bidCount + 1 })
        }
      }

      notify(id)
    },
    [appendBidRow, appendChatItem, refetchBids, refetchSnapshot, notify]
  )

  // -------------------------------------------------------------------------
  // subscribeBroadcast / unsubscribeBroadcast (on-demand per-listing channels)
  // -------------------------------------------------------------------------

  const subscribeBroadcast = useCallback((id: string) => {
    const entry = broadcastRefs.current.get(id)

    if (entry) {
      // Cancel pending destruction timer if within grace period
      if (entry.timer) {
        clearTimeout(entry.timer)
        entry.timer = undefined
      }
      entry.refCount++
      // Always refetch chat on new subscriber — ensures chat is loaded even
      // when the channel was already SUBSCRIBED before this component mounted.
      refetchChat(id)
      return
    }

    // Create new channel
    const supabase = getSupabaseBrowserClient()
    const channel = supabase
      .channel(`auction:${id}`)
      .on("broadcast", { event: "message" }, (payload: { payload?: unknown }) => {
        console.log(`[auction-rt] broadcast message on channel auction:${id}`, payload?.payload)
        const item = normalizeChatItem(payload?.payload)
        if (!item?.id) return
        appendChatItem(id, item)
      })
      .on(
        "broadcast",
        { event: "bid" },
        (payload: { payload?: Record<string, unknown> }) => {
          const p = payload?.payload
          if (!p) return
          applyBidRealtimePayload(id, p)
        }
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "auction_bids", filter: `listing_id=eq.${id}` },
        () => {
          refetchSnapshot(id)
          refetchBids(id)
          refetchChat(id)
        }
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "auction_chat_messages",
          filter: `listing_id=eq.${id}`,
        },
        () => {
          refetchChat(id)
        }
      )
      .subscribe((status) => {
        console.log(`[auction-rt] channel auction:${id} subscription status:`, status)
        const slot = auctionsRef.current.get(id)
        if (slot) {
          auctionsRef.current.set(id, { ...slot, isConnected: status === "SUBSCRIBED" })
          notify(id)
        }
        if (status === "SUBSCRIBED") {
          refetchSnapshot(id)
          refetchBids(id)
          refetchChat(id)
        }
        if (
          status === "CHANNEL_ERROR" ||
          status === "TIMED_OUT" ||
          status === "CLOSED"
        ) {
          refetchSnapshot(id)
          refetchBids(id)
          refetchChat(id)
        }
      })

    broadcastRefs.current.set(id, { channel, refCount: 1 })
  }, [appendChatItem, applyBidRealtimePayload, notify, refetchBids, refetchChat, refetchSnapshot])

  const unsubscribeBroadcast = useCallback((id: string) => {
    const entry = broadcastRefs.current.get(id)
    if (!entry) return

    entry.refCount--
    if (entry.refCount > 0) return

    // Grace period: 5s before destroying channel (handles mobile panel mount/unmount churn)
    entry.timer = setTimeout(() => {
      const e = broadcastRefs.current.get(id)
      if (e && e.refCount <= 0) {
        e.channel.unsubscribe()
        broadcastRefs.current.delete(id)
        // Mark slot as disconnected
        const slot = auctionsRef.current.get(id)
        if (slot) {
          auctionsRef.current.set(id, { ...slot, isConnected: false })
          notify(id)
        }
      }
    }, 5_000)
  }, [notify])

  // -------------------------------------------------------------------------
  // One-shot server-time sync (L-8 fix: clock-skew compensation for countdown)
  // -------------------------------------------------------------------------

  useEffect(() => {
    syncClock().catch(() => {})
  }, [])

  // -------------------------------------------------------------------------
  // Global pg_changes subscription on listings (no filter — single channel)
  // -------------------------------------------------------------------------

  useEffect(() => {
    const supabase = getSupabaseBrowserClient()
    const channel = supabase
      .channel("auction-realtime-global")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "listing_auctions" },
        (payload: { new?: Record<string, unknown> }) => {
          const next = payload?.new
          if (!next || typeof next.listing_id !== "string") return
          const id = next.listing_id

          if (!auctionsRef.current.has(id)) return

          if (next.status != null) {
            console.log(`[auction-rt] global pg_changes UPDATE listing_auctions id=${id} status=${next.status} ev=${next.event_version}`)
          }

          const nextEventVersion = toFiniteNumber(next.event_version)
          if (nextEventVersion != null) {
            evRef.current.set(id, Math.max(evRef.current.get(id) ?? 0, nextEventVersion))
          }

          const slot = auctionsRef.current.get(id)!
          const nextEndAt = next.end_at
          const nextHighestBid = toFiniteNumber(next.highest_bid)
          const nextBidCount = toFiniteNumber(next.bid_count)

          const updated: AuctionSlot = {
            ...slot,
            endAt:
              nextEndAt == null ? null : typeof nextEndAt === "string" ? nextEndAt : slot.endAt,
            highestBid:
              next.highest_bid == null
                ? null
                : (nextHighestBid ?? slot.highestBid),
            highestBidderId: Object.prototype.hasOwnProperty.call(next, "highest_bidder_id")
              ? typeof next.highest_bidder_id === "string"
                ? next.highest_bidder_id
                : null
              : slot.highestBidderId,
            auctionStatus:
              typeof next.status === "string" ? next.status : slot.auctionStatus,
            bidCount: nextBidCount ?? slot.bidCount,
            eventVersion:
              nextEventVersion != null
                ? Math.max(slot.eventVersion, nextEventVersion)
                : slot.eventVersion,
            paymentDeadline:
              Object.prototype.hasOwnProperty.call(next, "payment_deadline")
                ? typeof next.payment_deadline === "string"
                  ? next.payment_deadline
                  : null
                : slot.paymentDeadline,
          }

          auctionsRef.current.set(id, updated)
          notify(id)
        }
      )
      .subscribe()

    return () => {
      channel.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // -------------------------------------------------------------------------
  // Cleanup broadcast channels on unmount
  // -------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      broadcastRefs.current.forEach((entry) => {
        if (entry.timer) clearTimeout(entry.timer)
        entry.channel.unsubscribe()
      })
      broadcastRefs.current.clear()
    }
  }, [])

  // -------------------------------------------------------------------------
  // Context value (stable via useMemo — all callbacks are useCallback-stable)
  // -------------------------------------------------------------------------

  const contextValue = useMemo<AuctionRealtimeContextValue>(() => ({
    subscribe,
    unsubscribe,
    subscribeBroadcast,
    unsubscribeBroadcast,
    patchAuction,
    getSnapshot,
    addListener,
    refetchBids,
    refetchChat,
    refetchSnapshot,
    sendChatMessage,
  }), [subscribe, unsubscribe, subscribeBroadcast, unsubscribeBroadcast, patchAuction, getSnapshot, addListener, refetchBids, refetchChat, refetchSnapshot, sendChatMessage])

  return (
    <AuctionRealtimeContext.Provider value={contextValue}>
      {children}
    </AuctionRealtimeContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook for accessing the raw context (used internally and by tests/migrations)
// ---------------------------------------------------------------------------

export function useAuctionRealtimeCtx(): AuctionRealtimeContextValue {
  const ctx = useContext(AuctionRealtimeContext)
  if (!ctx) throw new Error("useAuctionRealtimeCtx must be within AuctionRealtimeProvider")
  return ctx
}

export default AuctionRealtimeProvider
